import * as React from 'react';
import { createElement } from 'react';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router';

import { getLastExchange, getIsAwaitingPaymentExchange } from '../../../../../Checkout/store/order/selectors';

import {
	getSelectedSegments,
	getLastSelectedFlight,
	getSelectedFlights,
	getSelectedPassengersIds,
	useSkipPassengers
} from '@entities/exchange';
import { RouterProps, useRouter } from '@shared/lib/router';

import { Layout } from '../Layout';
import { Passengers } from '../Passengers';
import { Segments } from '../Segments';
import { Flights } from '../Flights';
import { Request } from '../Request';
import { Payment } from '../Payment';
import { PaymentResult } from '../PaymentResult';

function compose<Props extends object>(...components: Array<(component: React.FC<Props>) => React.FC<Props>>) {
	return (component: React.FC<Props>) => components.reduceRight((comp, hoc) => hoc(comp), component);
}

function withProtection<T extends {}>(
	Component: React.FC<T>,
	options: { access: (props: T) => boolean; redirectTo: string }
): React.FC<T> {
	return (props: T) => {
		const router = useRouter();

		return !options.access(props)
			? createElement(Redirect, { to: `${router.basePath}${options.redirectTo}` })
			: createElement(Component, props);
	};
}

function withEmptyExchange<T extends {}>(Component: React.FC<T>) {
	return withProtection(Component, {
		access: () => {
			const lastExchange = useSelector(getLastExchange);

			return !lastExchange;
		},
		redirectTo: '/exchange/request'
	});
}

function withAwaitingPayment<T extends {}>(Component: React.FC<T>) {
	return withProtection(Component, {
		access: () => {
			return useSelector(getIsAwaitingPaymentExchange);
		},
		redirectTo: '/exchange'
	});
}

function NotFound() {
	const router = useRouter();

	return createElement(Redirect, { to: `${router.basePath}/exchange` });
}

export const exchangeRouter: RouterProps = {
	path: 'exchange',
	layout: Layout,
	children: [
		{
			index: true,
			element: createElement(
				compose(withEmptyExchange, component =>
					withProtection(component, {
						access: () => {
							const skipPassengers = useSkipPassengers();

							return !skipPassengers;
						},
						redirectTo: '/exchange/segments'
					})
				)(Passengers)
			)
		},
		{
			path: 'segments',
			element: createElement(
				compose(withEmptyExchange, component =>
					withProtection(component, {
						access: () => {
							const selectedPassengersIds = useSelector(getSelectedPassengersIds);

							return selectedPassengersIds.length > 0;
						},
						redirectTo: '/exchange'
					})
				)(Segments)
			)
		},
		{
			path: 'flights',
			element: createElement(
				compose(withEmptyExchange, component =>
					withProtection(component, {
						access: () => {
							const selectedSegments = useSelector(getSelectedSegments);
							const selectedFlights = useSelector(getSelectedFlights);

							return selectedSegments.length !== selectedFlights.length;
						},
						redirectTo: '/exchange/request'
					})
				)(Flights)
			)
		},
		{
			path: 'request',
			element: createElement(
				withProtection(Request, {
					access: () => {
						const lastExchange = useSelector(getLastExchange);
						const lastSelectedFlight = useSelector(getLastSelectedFlight);

						return !!lastExchange || !!lastSelectedFlight;
					},
					redirectTo: '/exchange'
				})
			)
		},
		{
			path: 'payment',
			exact: true,
			element: createElement(withAwaitingPayment(Payment))
		},
		{
			path: 'payment/:status(failed|success)',
			element: createElement(withAwaitingPayment(PaymentResult))
		},
		{
			path: '*',
			element: createElement(NotFound)
		}
	]
};
